import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { useRouter } from 'next/router'
import { useEffect, useRef, useState } from 'react'
import { ChevronDown, Menu, Search, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { ENTRY_KINDS } from '@/lib/kinds'
import BrandLogo from '@/components/kb/brand-logo'
import SearchBox from '@/components/kb/search-box'

interface NavItem {
    href: string
    label: string
    wide?: boolean
}

const NAV: NavItem[] = [
    { href: '/courses', label: 'Courses' },
    { href: '/samagri', label: 'Samagri' },
    { href: '/events', label: 'Events' },
    { href: '/places', label: 'Places', wide: true },
    { href: '/topics', label: 'Topics' },
    { href: '/goals', label: 'Goals', wide: true },
    { href: '/qa', label: 'Q&A' }
]

const isActive = (pathname: string, href: string) => pathname === href || pathname.startsWith(`${href}/`)

const SearchDialog: FC<{ open: boolean, onClose: () => void }> = ({ open, onClose }): ReactNode => {
    useEffect(() => {
        if (!open) return

        const onKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape') onClose()
        }

        const previous = document.body.style.overflow

        document.body.style.overflow = 'hidden'
        document.addEventListener('keydown', onKey)

        return () => {
            document.body.style.overflow = previous
            document.removeEventListener('keydown', onKey)
        }
    }, [open, onClose])

    if (!open) return null

    return (
        <div className='fixed inset-0 z-[60] flex items-start justify-center bg-foreground/50 px-4 pt-20 backdrop-blur-sm sm:pt-28' role='dialog' aria-modal='true' aria-label='Search'>
            <button type='button' className='absolute inset-0 cursor-default' aria-label='Close search' onClick={onClose} />

            <div className='relative w-full max-w-2xl rounded-2xl border-2 border-foreground bg-background p-4 shadow-brutal-lg sm:p-5'>
                <div className='mb-3 flex items-center justify-between'>
                    <p className='font-mono text-xs font-bold uppercase tracking-widest text-muted-foreground'>
                        Search everything
                    </p>

                    <button type='button' onClick={onClose} className='inline-flex size-8 items-center justify-center rounded-md border-2 border-foreground bg-card hover:bg-primary' aria-label='Close search'>
                        <X className='size-4' aria-hidden='true' />
                    </button>
                </div>

                <SearchBox autoFocus onNavigate={onClose} />

                <p className='mt-3 font-mono text-[11px] text-muted-foreground'>
                    Use the arrow keys to move, Enter to open, Esc to close.
                </p>
            </div>
        </div>
    )
}

const LibraryMenu: FC<{ active: boolean }> = ({ active }): ReactNode => {
    const router = useRouter()
    const ref = useRef<HTMLDivElement>(null)
    const [open, setOpen] = useState(false)

    useEffect(() => {
        const close = () => setOpen(false)

        router.events.on('routeChangeStart', close)

        return () => router.events.off('routeChangeStart', close)
    }, [router.events])

    useEffect(() => {
        if (!open) return

        const onPointer = (event: MouseEvent) => {
            if (ref.current && !ref.current.contains(event.target as Node)) setOpen(false)
        }

        const onKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape') setOpen(false)
        }

        document.addEventListener('mousedown', onPointer)
        document.addEventListener('keydown', onKey)

        return () => {
            document.removeEventListener('mousedown', onPointer)
            document.removeEventListener('keydown', onKey)
        }
    }, [open])

    return (
        <div ref={ref} className='relative' onMouseLeave={() => setOpen(false)}>
            <button
                type='button'
                aria-expanded={open}
                aria-haspopup='true'
                onClick={() => setOpen(value => !value)}
                onMouseEnter={() => setOpen(true)}
                className={cn('inline-flex items-center gap-1 rounded-lg px-3 py-2 text-sm font-semibold transition-colors hover:bg-secondary', active && 'bg-secondary')}
            >
                Library
                <ChevronDown className={cn('size-4 transition-transform', open && 'rotate-180')} aria-hidden='true' />
            </button>

            {open && (
                <div className='absolute left-1/2 top-full z-50 w-[560px] -translate-x-1/2 pt-2'>
                    <div className='rounded-xl border-2 border-foreground bg-card p-3 shadow-brutal-lg'>
                        <div className='grid grid-cols-2 gap-1'>
                            {ENTRY_KINDS.map(meta => {
                                const Icon = meta.icon

                                return (
                                    <Link key={meta.kind} href={`/library?kind=${meta.kind}`} className='flex items-start gap-3 rounded-lg p-2.5 hover:bg-secondary'>
                                        <span className={cn('flex size-9 shrink-0 items-center justify-center rounded-lg border-2 border-foreground', meta.tone)}>
                                            <Icon className='size-4' aria-hidden='true' />
                                        </span>

                                        <span className='min-w-0'>
                                            <span className='block text-sm font-bold'>{meta.plural}</span>
                                            <span className='block text-xs leading-snug text-muted-foreground'>{meta.description}</span>
                                        </span>
                                    </Link>
                                )
                            })}
                        </div>

                        <Link href='/library' className='mt-2 flex items-center justify-center rounded-lg border-2 border-foreground bg-primary px-3 py-2 text-sm font-bold hover:bg-brand-green'>
                            Browse the whole library
                        </Link>
                    </div>
                </div>
            )}
        </div>
    )
}

const SiteHeader: FC = (): ReactNode => {
    const router = useRouter()
    const [menuOpen, setMenuOpen] = useState(false)
    const [searchOpen, setSearchOpen] = useState(false)

    useEffect(() => {
        const close = () => {
            setMenuOpen(false)
            setSearchOpen(false)
        }

        router.events.on('routeChangeStart', close)

        return () => router.events.off('routeChangeStart', close)
    }, [router.events])

    useEffect(() => {
        const onKey = (event: KeyboardEvent) => {
            if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
                event.preventDefault()
                setSearchOpen(true)
            }
        }

        document.addEventListener('keydown', onKey)

        return () => document.removeEventListener('keydown', onKey)
    }, [])

    const pathname = router.pathname

    return (
        <>
            <div className='bg-foreground text-background'>
                <div className='mx-auto flex max-w-6xl items-center justify-center gap-3 px-4 py-2 text-center text-xs font-semibold sm:text-sm'>
                    <span className='hidden rounded-full border-2 border-background bg-brand-green px-2 py-0.5 font-mono text-[10px] font-bold uppercase text-foreground sm:inline'>
                        Tagged
                    </span>

                    <span>
                        Find where every samagri is used, across every remedy, event and place.
                    </span>

                    <Link href='/samagri' className='hidden underline underline-offset-4 hover:text-primary md:inline'>
                        Browse samagri
                    </Link>
                </div>
            </div>

            <header className='sticky top-0 z-40 border-b-[3px] border-foreground bg-card'>
                <div className='mx-auto flex h-16 max-w-6xl items-center justify-between gap-3 px-4 sm:px-6 md:h-[72px]'>
                    <Link href='/' aria-label='GeoVastu Gyan Kosh home' className='shrink-0'>
                        <BrandLogo />
                    </Link>

                    <nav aria-label='Main' className='hidden items-center gap-0.5 lg:flex'>
                        <Link href='/courses' className={cn('rounded-lg px-3 py-2 text-sm font-semibold transition-colors hover:bg-secondary', isActive(pathname, '/courses') && 'bg-secondary')}>
                            Courses
                        </Link>

                        <LibraryMenu active={isActive(pathname, '/library') || isActive(pathname, '/entries')} />

                        {NAV.slice(1).map(item => (
                            <Link
                                key={item.href}
                                href={item.href}
                                className={cn(
                                    'rounded-lg px-3 py-2 text-sm font-semibold transition-colors hover:bg-secondary',
                                    item.wide && 'hidden xl:inline-flex',
                                    isActive(pathname, item.href) && 'bg-secondary'
                                )}
                            >
                                {item.label}
                            </Link>
                        ))}
                    </nav>

                    <div className='flex items-center gap-2'>
                        <button
                            type='button'
                            onClick={() => setSearchOpen(true)}
                            className='inline-flex h-10 items-center gap-2 rounded-lg border-2 border-foreground bg-primary px-3 text-sm font-bold shadow-brutal-sm transition-transform hover:translate-x-px hover:translate-y-px hover:shadow-none'
                        >
                            <Search className='size-4' aria-hidden='true' />
                            <span className='hidden sm:inline'>Search</span>
                            <kbd className='hidden rounded border border-foreground/40 px-1 font-mono text-[10px] md:inline'>Ctrl K</kbd>
                        </button>

                        <button
                            type='button'
                            onClick={() => setMenuOpen(value => !value)}
                            aria-expanded={menuOpen}
                            aria-controls='mobile-menu'
                            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
                            className='inline-flex size-10 items-center justify-center rounded-lg border-2 border-foreground bg-card lg:hidden'
                        >
                            {menuOpen ? <X className='size-5' aria-hidden='true' /> : <Menu className='size-5' aria-hidden='true' />}
                        </button>
                    </div>
                </div>

                {menuOpen && (
                    <div id='mobile-menu' className='max-h-[calc(100vh-4rem)] overflow-y-auto border-t-2 border-foreground bg-background lg:hidden'>
                        <nav aria-label='Mobile' className='mx-auto max-w-6xl px-4 py-4 sm:px-6'>
                            <div className='grid grid-cols-2 gap-2'>
                                {[{ href: '/courses', label: 'Courses' }, { href: '/library', label: 'Library' }, ...NAV.slice(1)].map(item => (
                                    <Link
                                        key={item.href}
                                        href={item.href}
                                        className={cn('rounded-lg border-2 border-foreground px-3 py-2.5 text-sm font-bold', isActive(pathname, item.href) ? 'bg-primary' : 'bg-card')}
                                    >
                                        {item.label}
                                    </Link>
                                ))}
                            </div>

                            <p className='mb-2 mt-5 font-mono text-[11px] font-bold uppercase tracking-widest text-muted-foreground'>
                                Library by type
                            </p>

                            <div className='grid grid-cols-1 gap-1 sm:grid-cols-2'>
                                {ENTRY_KINDS.map(meta => {
                                    const Icon = meta.icon

                                    return (
                                        <Link key={meta.kind} href={`/library?kind=${meta.kind}`} className='flex items-center gap-3 rounded-lg px-2 py-2 hover:bg-secondary'>
                                            <span className={cn('flex size-8 items-center justify-center rounded-md border-2 border-foreground', meta.tone)}>
                                                <Icon className='size-4' aria-hidden='true' />
                                            </span>
                                            <span className='text-sm font-semibold'>{meta.plural}</span>
                                        </Link>
                                    )
                                })}
                            </div>
                        </nav>
                    </div>
                )}
            </header>

            <SearchDialog open={searchOpen} onClose={() => setSearchOpen(false)} />
        </>
    )
}

export default SiteHeader
